import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import LeaveReviewDialog from '@/components/website/LeaveReviewDialog';
import { MapPin, Phone, Mail, Clock, ExternalLink, Calendar, ChevronRight, Wind, HeartPulse, Bone, Activity, Flame, User, Sparkles, FileText, Hand, Waves, Brain, Droplets, Footprints, Accessibility, Gauge, Zap, Dumbbell, LineChart, Leaf, Users, Target, GraduationCap, Star } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';

// Precise coordinates of Rue des Colombes 11, 1868 Collombey (Valais)
const CLINIC_COORDS = [46.2820, 6.9455];

// Custom SVG marker matching the site's sky-blue palette
const clinicIcon = L.divIcon({
  className: 'clinic-marker',
  html: `
    <div style="position: relative; width: 40px; height: 52px;">
      <svg viewBox="0 0 40 52" width="40" height="52" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="marker-shadow" x="-20%" y="0%" width="140%" height="120%">
            <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="rgba(2,132,199,0.4)"/>
          </filter>
        </defs>
        <path d="M20 0 C 8.95 0 0 8.95 0 20 C 0 34.5 20 52 20 52 C 20 52 40 34.5 40 20 C 40 8.95 31.05 0 20 0 Z" fill="#0EA5E9" filter="url(#marker-shadow)"/>
        <circle cx="20" cy="20" r="8" fill="white"/>
        <circle cx="20" cy="20" r="4" fill="#0284C7"/>
      </svg>
    </div>
  `,
  iconSize: [40, 52],
  iconAnchor: [20, 52],
  popupAnchor: [0, -48],
});

const LOGO_URL =
  'https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/5gawhlad_ChatGPT%20Image%2030%20ao%C3%BBt%202026%2C%2007_31_30.png';

const APPOINTMENT_LINK = 'https://book.agenda.ch/services?companyId=19135';
const MAPS_LINK = 'https://maps.app.goo.gl/HycF9Mt3kMd1LHaE6';

const PATHOLOGIES = [
  {
    icon: Wind,
    title: 'Respiration & Souffle',
    desc: "Hyperventilation, asthme, Covid long, respiration dysfonctionnelle",
    href: '/respiration',
    tag: 'Spécialité',
  },
  {
    icon: Bone,
    title: 'Orthopédie',
    desc: "Récupération post-opératoire, entorses, tendinopathies, troubles musculo-squelettiques du sportif ou du sédentaire",
    href: null,
  },
  {
    icon: Activity,
    title: 'Rhumatologie',
    desc: "Arthroses, arthrites, douleurs chroniques, prise en charge globale des douleurs articulaires",
    href: null,
  },
  {
    icon: Flame,
    title: 'Traitement cicatriciel des brûlures',
    desc: "Massage, mobilisations et techniques spécifiques pour restaurer souplesse et sensibilité après brûlure",
    href: null,
  },
  {
    icon: Hand,
    title: 'Massage thérapeutique',
    desc: "Techniques manuelles ciblées pour soulager tensions musculaires, améliorer la circulation et favoriser la récupération",
    href: null,
  },
  {
    icon: Waves,
    title: 'Détente myofasciale',
    desc: "Libération des restrictions fasciales pour restaurer la mobilité, réduire les douleurs chroniques et améliorer la posture",
    href: null,
  },
  {
    icon: Brain,
    title: 'Sphère viscérale et crânienne',
    desc: "Approche douce des organes internes, du crâne et des membranes pour soulager troubles digestifs, migraines et tensions systémiques",
    href: null,
  },
  {
    icon: Droplets,
    title: 'Désencombrement bronchique',
    desc: "Techniques respiratoires manuelles et instrumentales pour évacuer les sécrétions et améliorer la ventilation pulmonaire",
    href: null,
  },
  {
    icon: Footprints,
    title: 'Rééducation à la marche et au mouvement',
    desc: "Reprogrammation du schéma de marche, coordination et gestes du quotidien après blessure, chirurgie ou perte de mobilité",
    href: null,
  },
  {
    icon: Accessibility,
    title: 'Prévention et traitement des chutes',
    desc: "Évaluation et amélioration de l'équilibre, de la force et de la coordination pour prévenir les chutes et sécuriser la marche",
    href: null,
  },
  {
    icon: Sparkles,
    title: 'Conscience corporelle et système nerveux',
    desc: "Rééducation sensorielle et régulation du système nerveux autonome pour retrouver ancrage, calme et fluidité de mouvement",
    href: null,
  },
  {
    icon: HeartPulse,
    title: 'Symptômes du COVID Long',
    desc: "Prise en charge globale de la fatigue chronique, dyspnée, brouillard cognitif et intolérance à l'effort post-COVID",
    href: null,
  },
  {
    icon: Gauge,
    title: 'Cohérence cardiaque avec biofeedback',
    desc: "Séances guidées par logiciel de biofeedback pour réguler la variabilité cardiaque, réduire le stress et équilibrer le système nerveux autonome",
    href: null,
  },
  {
    icon: Zap,
    title: 'Techniques d\'activation du nerf Vague',
    desc: "Stimulation ciblée du nerf vague pour améliorer la digestion, la récupération, le sommeil et la régulation émotionnelle",
    href: null,
  },
  {
    icon: Dumbbell,
    title: 'Rééducation à l\'effort',
    desc: "Programmes progressifs d'endurance, de force et d'efficience pour retrouver capacité physique, tolérance à l'effort et geste sportif optimal",
    href: null,
  },
  {
    icon: LineChart,
    title: 'Évaluation du système nerveux autonome',
    desc: "Analyse par logiciel professionnel de la variabilité cardiaque et rééducation ciblée pour équilibrer sympathique et parasympathique",
    href: null,
  },
  {
    icon: Leaf,
    title: 'Techniques de relaxation',
    desc: "Méthodes guidées de détente profonde pour apaiser le mental, relâcher les tensions physiques et retrouver un état de calme durable",
    href: null,
  },
  {
    icon: Users,
    title: 'Breathwork individuel ou en groupe',
    desc: "Séances de respiration consciente en accompagnement personnalisé ou en atelier collectif pour libérer les émotions et l'énergie",
    href: null,
  },
  {
    icon: Target,
    title: 'Renforcement musculaire',
    desc: "Programmes ciblés pour développer force, tonus et stabilité, adaptés à chaque niveau et à chaque pathologie",
    href: null,
  },
  {
    icon: GraduationCap,
    title: 'Éducation thérapeutique',
    desc: "Élément primordial dans notre approche pour favoriser l'autonomie des patients dans leur compréhension de la santé et des moyens de la restaurer",
    href: null,
  },
];

const TEAM = [
  {
    name: 'Julien Jaccard',
    title: 'Physiothérapeute · Fondateur du cabinet',
    photo:
      'https://customer-assets.emergentagent.com/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/c1aoalsm_ChatGPT%20Image%2026%20juin%202026%2C%2014_21_40.png',
    specialties: ['Respiration', 'Nerf vague', 'Chaînes physiologiques', '25+ ans d\'expérience'],
    cvUrl: 'https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/vf1nllf9_MON%20CV.pdf',
    bio:
      "J'exerce la Physiothérapie depuis 1999 à l'Hôpital Riviera Chablais — j'ai pratiqué dans tous les services : Orthopédie/Traumatologie, Chirurgie Viscérale, Médecine, Soins Continus et Intensifs. Depuis 2019, mon activité est devenue ambulatoire à 100%. C'est à cette période, marquée par l'arrivée du COVID-19, que j'ai commencé à me passionner pour les troubles respiratoires et les symptômes associés, et n'ai pas cessé d'étudier, de me former, et de pratiquer sur moi-même toutes les techniques apprises. Mes prises en charge n'ont cessé de s'améliorer, ma compréhension du corps et des moyens de l'aider à retrouver son équilibre également ; aujourd'hui, ce métier est devenu un mode de vie, un appel quotidien qui rend mes relations avec les patients uniques et transformatrices.",
    isPlaceholder: false,
  },
  {
    name: 'Nouveau collègue',
    title: 'Physiothérapeute · Bientôt parmi nous',
    photo: null,
    specialties: ['À venir'],
    bio: "Un(e) second(e) physiothérapeute rejoindra prochainement le cabinet. Plus d'informations très bientôt.",
    isPlaceholder: true,
  },
];

const CabinetHomePage = () => {
  const [reviews, setReviews] = useState([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
        setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
      } catch (err) {
        // Silent fail — section simply won't render if backend unreachable
        setReviews([]);
      }
    };
    fetchReviews();

    // Listen for external triggers (mobile menu button)
    const openHandler = () => setReviewDialogOpen(true);
    window.addEventListener('open-leave-review', openHandler);
    return () => window.removeEventListener('open-leave-review', openHandler);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ fontFamily: 'Figtree, sans-serif' }}>
      {/* Background — real water image, fixed, gently tinted */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "url('https://customer-assets-lqy194kg.emergentagent.net/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/hrao4qok_ChatGPT%20Image%2027%20ao%C3%BBt%202026%2C%2008_49_20.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Soft veil for legibility over the water photo */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }}
      />

      {/* Watermark logo — very faint */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[1] flex items-center justify-center"
        style={{ opacity: 0.045 }}
      >
        <img src={LOGO_URL} alt="" className="max-w-[70vw] md:max-w-[45vw] w-full h-auto" />
      </div>

      <div className="relative z-10">
        <SiteHeaderV2 />

        {/* Bouton "Laisser un avis" — desktop only, top-left below header */}
        <button
          type="button"
          onClick={() => setReviewDialogOpen(true)}
          className="hidden lg:inline-flex items-center gap-2 absolute left-6 top-24 z-20 px-5 py-2.5 rounded-full text-sm font-medium bg-white/85 backdrop-blur-md border border-slate-200 shadow-md hover:shadow-lg hover:bg-white transition-all"
          style={{ color: '#0284C7' }}
          data-testid="leave-review-btn-desktop"
        >
          <Star size={16} className="fill-amber-400 text-amber-400" />
          Laisser un avis
        </button>

        <LeaveReviewDialog
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
        />

        {/* ============ HERO ============ */}
        <section className="pt-10 pb-20 sm:pt-14 sm:pb-28">
          <div className="max-w-5xl mx-auto px-6 sm:px-10">
            <div className="flex flex-col items-center text-center">
              {/* Lien vers le site de la respiration — DESKTOP uniquement.
                  Sur mobile, le header est replié dans le burger : on met donc
                  "Prendre rendez-vous" en évidence juste au-dessus du logo. */}
              <Link
                to="/respiration"
                className="hidden sm:inline-flex items-center justify-center px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 mb-8 whitespace-nowrap"
                style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF', boxShadow: '0 6px 18px -6px rgba(14,165,233,0.5)' }}
                data-testid="hero-respiration-site-link"
              >
                Le site de la respiration et son application
              </Link>

              {/* Bouton "Prendre rendez-vous" — MOBILE uniquement, bien visible
                  dès l'ouverture, sans avoir à ouvrir le menu burger. */}
              <a
                href={APPOINTMENT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="sm:hidden inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-base font-semibold text-white shadow-lg mb-8 whitespace-nowrap"
                style={{ backgroundColor: '#0EA5E9', boxShadow: '0 6px 18px -6px rgba(14,165,233,0.5)' }}
                data-testid="hero-appointment-mobile"
              >
                <Calendar size={18} />
                Prendre rendez-vous
              </a>

              <img
                src={LOGO_URL}
                alt="Physiothérapie Les Mains Nuages"
                className="w-80 sm:w-[28rem] lg:w-[32rem] h-auto mb-8"
                data-testid="hero-brand-logo"
              />
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-4"
                style={{ color: '#0284C7' }}
              >
                Physiothérapie · Valais
              </p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6 tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
              >
                Une physiothérapie <span className="font-normal" style={{ color: '#0EA5E9' }}>différente, unique dans son approche</span>
              </h1>
              <p className="max-w-3xl text-lg text-slate-600 mb-10 leading-relaxed">
                Une compréhension scientifique et directe de l&apos;intelligence naturelle du corps humain, 
                qui fait ses preuves depuis son commencement en 2019, basée sur le bon sens physiologique, 
                la compréhension par chacun de ses fonctionnements individuels, et sur le retour à l&apos;écoute, 
                le respect et l&apos;accompagnement raisonné du corps dans ses signaux d&apos;alerte 
                et ses processus de guérison.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <a
                  href={APPOINTMENT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: '#0EA5E9' }}
                  data-testid="hero-book-btn"
                >
                  <Calendar size={20} />
                  Prendre rendez-vous
                </a>
                <Link
                  to="/respiration"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium border transition-all hover:bg-white"
                  style={{ borderColor: '#CBD5E1', color: '#334155', backgroundColor: 'rgba(255,255,255,0.7)' }}
                  data-testid="hero-respiration-link"
                >
                  Espace respiration <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============ AVIS PATIENTS (vrais avis approuvés) ============ */}
        {reviews.length > 0 && (
          <section className="py-16 sm:py-20 border-t border-slate-100" data-testid="reviews-section">
            <div className="max-w-6xl mx-auto px-6 sm:px-10">
              <div className="text-center mb-12">
                <p
                  className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-3"
                  style={{ color: '#0284C7' }}
                >
                  Témoignages
                </p>
                <h2
                  className="text-3xl sm:text-4xl tracking-tight"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
                >
                  Ce que disent nos patients
                </h2>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {reviews.map((r) => (
                  <article
                    key={r.id}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col"
                    data-testid={`review-${r.id}`}
                  >
                    <div className="flex gap-1 mb-3" aria-label={`${r.rating} étoiles sur 5`}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}
                        />
                      ))}
                    </div>
                    {r.title && (
                      <p className="text-slate-900 font-semibold mb-2">{r.title}</p>
                    )}
                    <p className="text-slate-700 leading-relaxed italic flex-1">« {r.comment} »</p>
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-slate-900 font-semibold text-sm">{r.name}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
        {/* ============ PHILOSOPHIE ============ */}
        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-6 sm:px-10">
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 sm:p-12">
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-4"
                style={{ color: '#0284C7' }}
              >
                Notre philosophie
              </p>
              <h2
                className="text-3xl sm:text-4xl mb-6 tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
              >
                Prendre soin, <span style={{ color: '#0EA5E9' }}>à l&apos;écoute</span> de chacun
              </h2>
              <div className="space-y-4 text-base sm:text-lg leading-relaxed text-slate-700">
                <p>
                  <strong>Les Mains Nuages</strong> est une pratique bien connue du TaiJi, qui représente notre vision thérapeutique : 
                  le retour à un corps senti, parfaitement capable de s&apos;équilibrer par une capacité de relâchement juste, 
                  dans le mouvement comme au repos ; un corps fluide, solidement ancré par sa base, capable de s&apos;adapter sans résistance 
                  aux contraintes, libre et léger, capable de force, de puissance et d&apos;endurance, sans perte d&apos;énergie inutile.
                </p>
                <p>
                  Vivre dans un tel corps est beaucoup plus simple qu&apos;on ne le pense. Cela demande simplement une compréhension 
                  de son fonctionnement que nous nous efforçons de transmettre depuis des années, et qui amène, à ceux qui le comprennent 
                  et le pratiquent, à des changements radicaux dans leur façon d&apos;appréhender le corps, le mouvement, la vie de tous les jours, 
                  les relations sociales, et la simple capacité à ce que nous désirons tous : <em>vivre dans un corps sans douleurs, sans restrictions, équilibré, et plein d&apos;énergie.</em>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PATHOLOGIES ============ */}
        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-6 sm:px-10">
            <div className="text-center mb-12">
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-3"
                style={{ color: '#0284C7' }}
              >
                Pathologies prises en charge et traitements spécifiques
              </p>
            </div>
            <div className="grid gap-5 sm:gap-6 md:grid-cols-2">
              {PATHOLOGIES.map((p) => {
                const Icon = p.icon;
                const inner = (
                  <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-7 hover:shadow-md transition-all hover:border-sky-200 hover:-translate-y-0.5">
                    <div className="flex items-start gap-4">
                      <div
                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}
                      >
                        <Icon size={22} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h3 className="text-lg font-semibold text-slate-900">{p.title}</h3>
                          {p.tag && (
                            <span
                              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}
                            >
                              {p.tag}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                        {p.href && (
                          <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#0EA5E9' }}>
                            En savoir plus <ChevronRight size={16} />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
                return p.href ? (
                  <Link key={p.title} to={p.href} data-testid={`pathology-${p.title}`}>{inner}</Link>
                ) : (
                  <div key={p.title} data-testid={`pathology-${p.title}`}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ ÉQUIPE ============ */}
        <section className="py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-6 sm:px-10">
            <div className="text-center mb-12">
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-3"
                style={{ color: '#0284C7' }}
              >
                Notre équipe
              </p>
              <h2
                className="text-3xl sm:text-4xl tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
              >
                Vos <span style={{ color: '#0EA5E9' }}>thérapeutes</span>
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {TEAM.map((m) => (
                <div
                  key={m.name}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-7"
                  data-testid={`team-card-${m.isPlaceholder ? 'placeholder' : 'julien'}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {m.photo ? (
                        <img
                          src={m.photo}
                          alt={m.name}
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2"
                          style={{ borderColor: '#E0F2FE', objectPosition: 'center 25%' }}
                        />
                      ) : (
                        <div
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center"
                          style={{ backgroundColor: '#F1F5F9', color: '#94A3B8' }}
                        >
                          <User size={40} strokeWidth={1.5} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900">{m.name}</h3>
                      <p className="text-sm mb-3" style={{ color: '#0284C7' }}>{m.title}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{m.bio}</p>
                      {m.cvUrl && (
                        <Link
                          to={`/view-document?doc=${encodeURIComponent(m.cvUrl)}&title=${encodeURIComponent('Curriculum vitae - ' + m.name)}&back=/`}
                          className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-medium border transition-all hover:shadow-md"
                          style={{ borderColor: '#0EA5E9', color: '#0284C7', backgroundColor: '#F0F9FF' }}
                          data-testid={`cv-btn-${m.name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <FileText size={16} />
                          Curriculum vitae
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ COORDONNÉES + CARTE ============ */}
        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-6 sm:px-10">
            <div className="text-center mb-12">
              <p
                className="text-xs sm:text-sm uppercase tracking-[0.28em] font-semibold mb-3"
                style={{ color: '#0284C7' }}
              >
                Nous rendre visite
              </p>
              <h2
                className="text-3xl sm:text-4xl tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
              >
                Coordonnées <span style={{ color: '#0EA5E9' }}>& accès</span>
              </h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Contact card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-8 flex flex-col">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Physiothérapie Les Mains Nuages</h3>

                <ul className="space-y-4 flex-1">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                      <MapPin size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-0.5">Adresse</p>
                      <p className="text-slate-800 font-medium">Rue des Colombes 11</p>
                      <p className="text-slate-800">1868 Collombey, Valais · Suisse</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                      <Mail size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-0.5">Email</p>
                      <a
                        href="mailto:julienjaccard@mains-nuages.ch"
                        className="text-slate-800 font-medium hover:text-sky-600 transition-colors break-all"
                        data-testid="contact-email"
                      >
                        julienjaccard@mains-nuages.ch
                      </a>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                      <Clock size={18} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-0.5">Sur rendez-vous</p>
                      <p className="text-slate-800">Lundi — Vendredi</p>
                      <p className="text-slate-600 text-sm">Réservation en ligne sur agenda.ch</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={APPOINTMENT_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: '#0EA5E9' }}
                    data-testid="contact-book-btn"
                  >
                    <Calendar size={18} />
                    Prendre rendez-vous
                  </a>
                  <a
                    href={MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium border transition-all hover:bg-slate-50"
                    style={{ borderColor: '#CBD5E1', color: '#334155' }}
                  >
                    Itinéraire <ExternalLink size={16} />
                  </a>
                </div>
              </div>

              {/* Interactive Leaflet Map (OpenStreetMap) */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden min-h-[360px] flex flex-col" data-testid="map-container">
                <MapContainer
                  center={CLINIC_COORDS}
                  zoom={16}
                  scrollWheelZoom={false}
                  style={{ width: '100%', height: '100%', minHeight: '400px', zIndex: 1 }}
                  data-testid="leaflet-map"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={CLINIC_COORDS} icon={clinicIcon}>
                    <Popup>
                      <div style={{ minWidth: 200, fontFamily: 'Figtree, sans-serif' }}>
                        <p style={{ fontWeight: 600, color: '#0F172A', margin: 0, fontSize: 14 }}>
                          Physiothérapie Les Mains Nuages
                        </p>
                        <p style={{ color: '#475569', margin: '4px 0', fontSize: 13, lineHeight: 1.4 }}>
                          Rue des Colombes 11<br />
                          1868 Collombey<br />
                          Valais, Suisse
                        </p>
                        <a
                          href={MAPS_LINK}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginTop: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#0284C7',
                            textDecoration: 'none',
                          }}
                        >
                          Itinéraire Google Maps ↗
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA section removed as requested */}

        <SiteFooterV2 />
      </div>
    </div>
  );
};

export default CabinetHomePage;
